'use client';
import { useMemo } from 'react';

export type AppRole =
  | 'super_admin' |'admin' |'calling_executive' |'marketing_executive' |'sales_executive';

export interface RolePermissions {
  // Navigation
  canAccessUserManagement: boolean;
  canAccessProductManagement: boolean;
  canAccessReports: boolean;
  canAccessSchedule: boolean;
  canAccessLeadManagement: boolean;

  // Lead actions
  canCreateLead: boolean;
  canEditLead: boolean;
  canDeleteLead: boolean;
  canBulkDeleteLeads: boolean;
  canAssignLead: boolean;

  // User management actions
  canAddUser: boolean;
  canEditUser: boolean;
  canDeleteUser: boolean;
  canToggleUserStatus: boolean;

  // Product management actions
  canAddProduct: boolean;
  canEditProduct: boolean;
  canDeleteProduct: boolean;

  // Reports
  canExportReports: boolean;
  canViewTeamReports: boolean;
}

/**
 * Role tier mapping:
 *  Admin tier    → super_admin, admin
 *  Manager tier  → (none currently — reserved for future)
 *  Executive tier→ calling_executive, marketing_executive, sales_executive
 */
export function getPermissions(role: AppRole | null | undefined): RolePermissions {
  const isAdmin = role === 'super_admin' || role === 'admin';
  const isExecutive =
    role === 'calling_executive' ||
    role === 'marketing_executive' ||
    role === 'sales_executive';

  return {
    // Navigation
    canAccessUserManagement: isAdmin,
    canAccessProductManagement: isAdmin,
    canAccessReports: true, // all roles can access reports
    canAccessSchedule: true, // all roles
    canAccessLeadManagement: true, // all roles

    // Lead actions
    canCreateLead: true,
    canEditLead: isAdmin,
    canDeleteLead: isAdmin,
    canBulkDeleteLeads: isAdmin,
    canAssignLead: isAdmin || role === 'marketing_executive',

    // User management
    canAddUser: isAdmin,
    canEditUser: isAdmin,
    canDeleteUser: role === 'super_admin',
    canToggleUserStatus: isAdmin,

    // Product management
    canAddProduct: isAdmin,
    canEditProduct: isAdmin,
    canDeleteProduct: role === 'super_admin',

    // Reports
    canExportReports: isAdmin,
    canViewTeamReports: isAdmin,
  };
}

export function useRolePermissions(role: AppRole | null | undefined): RolePermissions {
  return useMemo(() => getPermissions(role), [role]);
}
